import * as React from "react";
import { CallClient, GroupCallLocator, LocalAudioStream, LocalVideoStream, TeamsMeetingLinkLocator } from '@azure/communication-calling';
import { AzureCommunicationTokenCredential, CommunicationUserIdentifier } from '@azure/communication-common';
import { CallAndChatLocator } from '@azure/communication-react';
import { setLogLevel } from '@azure/logger';
import { initializeIcons } from '@fluentui/react';
import { Flex, Loader } from "@fluentui/react-northstar";
import {
    createGroupId,
    ensureJoinableCallLocatorPushedToUrl,
    ensureJoinableChatThreadPushedToUrl,
    ensureJoinableTeamsLinkPushedToUrl,
    getGroupIdFromUrl,
    getTeamsLinkFromUrl,
} from './utils/AppUtils';
import { createUserToken, getAcsFields } from '../../api/acs-api';
import { updateQueueExternalUser } from '../../api/publicapp-api';
import CallScreen from './views/call-screen';
import HomeScreen, { AcsFields } from './views/home-screen';
import { joinThread } from './utils/joinThread';
import { getThread } from './utils/getThread';
import { getExistingThreadIdFromURL } from './utils/getThreadId';
import { withTranslation, WithTranslation } from "react-i18next";
import { TFunction } from "i18next";
import "./acs.scss";
import { createBeepAudioStreamToSend, createVideoMediaStreamToSend } from "./utils/createStream";

setLogLevel('warning');
initializeIcons();

interface Credentials {
    userId: CommunicationUserIdentifier;
    token: string;
}

interface CallWithChatArgs {
    credentials?: Credentials;
    endpointUrl: string;
    displayName: string;
    locator?: CallAndChatLocator | TeamsMeetingLinkLocator;
    page?: AppPages;
    callWithChatArgs?: CallWithChatArgs | undefined;
    callInProgress?: boolean;
    acsFields?: any;
}

interface IAcsProps extends WithTranslation {
    queueData: any;
    userStarted: Function;
}

type AppPages = 'home' | 'call' | 'error';

class Acs extends React.Component<IAcsProps, CallWithChatArgs> {
    private localize: TFunction;

    constructor(props: IAcsProps) {
        super(props);
        this.localize = this.props.t;

        this.state = {
            endpointUrl: "",
            displayName: "",
            page: "home",
            callWithChatArgs: undefined,
            callInProgress: false,
            acsFields: null,
        }
    }

    public componentDidMount = () => {
        window.scrollTo(0, document.body.scrollHeight);
        this.getAcsFieldsData();
    }

    public startCustomVideoCall = async (displayName: string, teamsLink: string, token: string, userId: string) => {
        const callClient = new CallClient();
        const credential = new AzureCommunicationTokenCredential(token);
        const callAgent = await callClient.createCallAgent(credential, { displayName });

        const deviceManager = await callClient.getDeviceManager();
        const cameras = await deviceManager.getCameras();
        const camera = cameras[0];

        const cameraStream = await new LocalVideoStream(camera).getMediaStream();
        const video = document.createElement('video');
        video.srcObject = cameraStream;
        video.autoplay = true;
        await video.play();

        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 480;
        const ctx = canvas.getContext('2d')!;

        const draw = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            ctx.filter = 'blur(50px)';
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            ctx.filter = 'none';
            ctx.font = '40px Arial';
            ctx.fillStyle = 'white';
            ctx.textAlign = 'center';
            ctx.fillText('STUDIO365', canvas.width / 2, 50);
            requestAnimationFrame(draw);
            console.log("AAAAAAAAAAAA");
        };
        draw();

        const customStream = (canvas as any).captureStream(30);
        const localVideoStream = new LocalVideoStream(customStream);

        await callAgent.join({ meetingLink: teamsLink }, {
            videoOptions: { localVideoStreams: [localVideoStream] },
            audioOptions: { muted: true }
        });
    };

    public getAcsFieldsData = async () => {
        const response = await getAcsFields();

        if (response.status === 200 && response.data) {
            this.setState({
                acsFields: response.data.acsFields,
            })
        }
    }

    public onStartCall = () => {
        const inFullScreen = document.getElementsByClassName('acs-fullscreen');

        if (inFullScreen.length > 0) {

        } else {
            window.scrollTo(0, 0);
        }

        const element = document.querySelector<HTMLElement>("body")!;

        element.style.overflowY = 'hidden';

        this.setState({
            callInProgress: true,
        })
    }

    public onEndCall = () => {
        window.scrollTo(0, document.body.scrollHeight);

        const element = document.querySelector<HTMLElement>("body")!;

        element.removeAttribute("style");

        this.setState({
            callInProgress: false,
        })
    }

    public generateCallWithChatArgs = async (
        displayName: string,
        acsFields?: AcsFields[],
        teamsLink?: TeamsMeetingLinkLocator,
    ): Promise<CallWithChatArgs> => {
        const responseCredentials = await createUserToken();
        const acsFieldsRequest: AcsFields[] = [];

        if (acsFields) {
            acsFields.map(f => acsFieldsRequest.push({ name: f.name, value: f.value }));
        }

        await updateQueueExternalUser(this.props.queueData.id,
            {
                userName: displayName,
                ...(acsFields && { acsFields: acsFieldsRequest }),
            }
        );

        const credentials = {
            userId: {
                communicationUserId: responseCredentials.data.userIdentifier.id,
            },
            token: responseCredentials.data.accessToken.token,
        };

        const endpointUrl = responseCredentials.data.endpoint;

        let locator: CallAndChatLocator | TeamsMeetingLinkLocator;

        // Check if we should join a teams meeting, or an ACS CallWithChat
        teamsLink = teamsLink ?? getTeamsLinkFromUrl();
        if (teamsLink) {
            locator = teamsLink;
            ensureJoinableTeamsLinkPushedToUrl(teamsLink);
        } else {
            const callLocator = this.callLocatorGen();

            const chatThreadId = await getThread();
            await joinThread(chatThreadId, credentials.userId.communicationUserId, displayName);
            ensureJoinableChatThreadPushedToUrl(chatThreadId);

            locator = {
                callLocator,
                chatThreadId
            };
        }

        return {
            displayName,
            endpointUrl,
            credentials,
            locator,
        };
    }

    public callLocatorGen = (): GroupCallLocator => {
        const callLocator = getGroupIdFromUrl() || createGroupId();
        ensureJoinableCallLocatorPushedToUrl(callLocator);
        return callLocator;
    };

    public renderAcs = (): JSX.Element => {
        const joiningExistingCallWithChat: boolean =
            (!!getGroupIdFromUrl() && !!getExistingThreadIdFromURL()) || !!getTeamsLinkFromUrl();

        switch (this.state.page) {
            case 'home': {
                return (
                    <div className="acs-intro">
                        <HomeScreen
                            // Show a simplified join home screen if joining an existing call
                            joiningExistingCall={joiningExistingCallWithChat}
                            startCallHandler={async (homeScreenDetails) => {
                                this.setState({ page: "call" });

                                try {
                                    const callWithChatArgs = await this.generateCallWithChatArgs(
                                        homeScreenDetails.displayName,
                                        homeScreenDetails?.acsFields,
                                        homeScreenDetails?.teamsLink,
                                    );

                                    console.log(callWithChatArgs);
                                    this.setState({
                                        callWithChatArgs: callWithChatArgs
                                    }, () => {
                                        //this.props.userStarted();
                                    });
                                } catch (e) {
                                    console.log(e);
                                    this.setState({ page: "error" });
                                }
                            }}
                            meetingUrl={this.props.queueData.meetingUrl.split("&")[0]}
                            userProfile={this.props.queueData.userProfile}
                            acsFields={this.state.acsFields}
                            startCustomVideoCall={async (displayName: string) => {
                                const creds = await createUserToken();
                                const userId = creds.data.userIdentifier.id;
                                const token = creds.data.accessToken.token;
                               
                                await this.startCustomVideoCall(displayName, this.props.queueData.meetingUrl.replace("&webjoin=true&unified=true", ""), token, userId);
                            }}
                        />
                    </div>
                );
            }
            case 'call': {
                if (
                    !this.state.callWithChatArgs?.credentials?.token ||
                    !this.state.callWithChatArgs.credentials?.userId ||
                    !this.state.callWithChatArgs.displayName ||
                    !this.state.callWithChatArgs.locator ||
                    !this.state.callWithChatArgs.endpointUrl
                ) {
                    return (
                        <div className="loader">
                            <Loader size="small" label={this.localize("Carregando")} />
                        </div>
                    );
                }
                return (
                    <div className={this.state.callInProgress ? `acs-intro acs-fullscreen` : `acs-intro`}>
                        <CallScreen
                            token={this.state.callWithChatArgs.credentials.token}
                            userId={this.state.callWithChatArgs.credentials.userId}
                            displayName={this.state.callWithChatArgs.displayName}
                            locator={this.state.callWithChatArgs.locator}
                            endpoint={this.state.callWithChatArgs.endpointUrl}
                            callStarted={this.onStartCall}
                            callEnded={this.onEndCall}
                            queueData={this.props.queueData}
                        />
                    </div>
                );
            }
            default:
                return <>Invalid page</>;
        }
    }

    public render = (): JSX.Element => {
        return (
            <Flex className="acs-main" gap="gap.medium" column>
                {this.renderAcs()}
            </Flex>
        );
    };
}

const acsWithTranslation = withTranslation()(Acs);
export default acsWithTranslation;